import type { OptionSource, OptionSourcePort, SourcedOption } from "@/shared/contracts/ports";
import { HttpError, request } from "@/shared/http/client";
import { i18n } from "@/shared/i18n";

/** Opções calculadas por função ativa, executada na fronteira de leitura do servidor. */
interface RowsResponse {
	rows?: Array<Record<string, unknown>>;
}

export function createOptionSourcePort(): OptionSourcePort {
	/**
	 * Uma consulta por combinação, enquanto a aba está aberta.
	 *
	 * Um formulário com o mesmo campo repetido numa lista de subitens pediria as
	 * mesmas linhas uma vez por item. A chave inclui os inputs porque é justamente
	 * eles que mudam a resposta — "os horários livres do dia 2" não são os do dia 3.
	 */
	const inFlight = new Map<string, Promise<{ options: SourcedOption[]; notice: string | null }>>();

	async function read(
		source: OptionSource,
	): Promise<{ options: SourcedOption[]; notice: string | null }> {
		const params = new URLSearchParams(source.inputs ?? {});
		const query = params.toString();
		const url = `/v3/artifacts/${source.artifact}/rows${query ? `?${query}` : ""}`;

		try {
			const payload = await request<RowsResponse>(url);
			const options = (payload.rows ?? []).flatMap((row) => toOption(row, source));
			if (options.length === 0) {
				return { options: [], notice: "a consulta não devolveu nenhuma opção agora" };
			}
			return { options, notice: null };
		} catch (error) {
			return { options: [], notice: noticeFor(error, source.artifact) };
		}
	}

	return {
		options(source) {
			if (!source.artifact || !source.valueField) {
				return Promise.resolve({
					options: [],
					notice: "este campo diz que as opções vêm de uma consulta, mas não diz qual",
				});
			}
			const key = JSON.stringify([
				source.artifact,
				source.valueField,
				source.labelField,
				source.inputs ?? {},
			]);
			let pending = inFlight.get(key);
			if (!pending) {
				pending = read(source);
				inFlight.set(key, pending);
			}
			return pending;
		},
	};
}

/**
 * Uma linha vira uma opção — ou nenhuma.
 *
 * Sem valor não há o que guardar, e uma opção com valor vazio é a que a pessoa
 * escolhe sem perceber e o formulário salva em branco. Sem rótulo, o valor serve:
 * é o que o campo guarda de qualquer jeito.
 */
function toOption(row: Record<string, unknown>, source: OptionSource): SourcedOption[] {
	const raw = row[source.valueField];
	if (raw === null || raw === undefined || raw === "") return [];
	const value = String(raw);
	const label = row[source.labelField];
	return [{ value, label: label === null || label === undefined ? value : String(label) }];
}

function noticeFor(error: unknown, artifact: string): string {
	if (error instanceof HttpError) {
		// A rota devolve 404 tanto para um code que não existe quanto para uma
		// execução que não achou o artefato, e daqui não dá para separar os dois —
		// a frase cobre os dois casos sem afirmar o que não sabe.
		if (error.kind === "notFound")
			return i18n.global.t("dynamicForm.optionSource.notFound", { artifact });
		if (error.kind === "forbidden")
			return i18n.global.t("dynamicForm.optionSource.forbidden", { artifact });
		if (error.kind === "validation") return error.message;
	}
	return i18n.global.t("dynamicForm.optionSource.failed", { artifact });
}
